import React, { useEffect, useState } from "react";

import {
  ListItemText,
  ListItem,
  Checkbox as MICheckbox,
  Collapse,
  CheckboxProps,
} from "@mui/material";
import withStyles from "@mui/styles/withStyles";
import { ExpandLess, ExpandMore } from "@mui/icons-material";
import { Category, Option } from "Components/AppGrid/shipeezi-new-grid";
import { useTranslation } from "react-i18next";

import AutoSuggestFilter from "../AutoSuggestFilter";
import useStyles, { AutoSuggestFilterWrapper } from "./styles";

type Props = {
  category?: Category;
  onChange?: (category: Category) => void;
};

const StyledCheckbox = withStyles({
  root: {
    "&$checked": {
      color: "#308CDB",
    },
  },
  checked: {},
})((props: CheckboxProps) => <MICheckbox color="default" {...props} />);

const CategoryItem = (props: Props) => {
  const { category, onChange } = props;

  const { t: translate } = useTranslation();
  const classes = useStyles();

  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (category?.optionsSelected?.length) setOpen(true);
  }, []);

  const handleClickOpen = () => {
    setOpen(!open);
  };

  const optionIsChecked = (option: string) => {
    let optionFound: string | undefined;

    if (category?.optionsSelected && category?.optionsSelected?.length > 0) {
      optionFound = category?.optionsSelected?.find((optionSelected) => optionSelected === option);
      if (optionFound) return true;
    }

    return false;
  };

  const onSelectOption = (option: string, checked: boolean) => {
    let optionsSelectedUpdated = [...(category?.optionsSelected || [])];

    if (checked) {
      optionsSelectedUpdated.push(option);
    } else {
      optionsSelectedUpdated = optionsSelectedUpdated.filter(
        (optionSelected) => optionSelected !== option
      );
    }

    if (onChange)
      onChange({
        ...category,
        optionsSelected: optionsSelectedUpdated,
      } as Category);
  };

  const onAddOption = (option: Option) => {
    if (!category) return;

    const options = [...(category?.options || [])] as Option[];
    const optionsSelected = [...(category?.optionsSelected || [])];

    if (!options.some((existentOption: Option) => existentOption.value === option.value))
      options.push(option);

    if (!optionsSelected.includes(option.value)) optionsSelected.push(option?.value);

    onChange?.({
      ...category,
      options,
      optionsSelected,
    } as Category);
  };

  const clearSelectedOptions = () => {
    if (!category) return;

    if (onChange) {
      onChange({
        ...category,
        optionsSelected: [],
      });
    }
  };

  const renderOptions = () => {
    if (category && category?.options && category?.options?.length > 0) {
      return category?.options?.map((option: Option | string) => {
        if (typeof option !== "string") {
          return (
            <div
              data-cy={option.value ? option?.value.toString().toLowerCase() : null}
              key={option?.value}
              className={classes.containerOptions}
            >
              <StyledCheckbox
                data-cy={option.value ? `checkbox-${option?.value.toString().toLowerCase()}` : null}
                checked={optionIsChecked(option?.value)}
                className={classes.checkbox}
                onChange={(event) => onSelectOption(option?.value, event.target.checked)}
              />
              <span
                data-cy={option.value ? `title-${option?.value.toString().toLowerCase()}` : null}
                className={classes.textOption}
              >
                {option?.label}
              </span>
            </div>
          );
        }

        return (
          <div data-cy={option.toLowerCase()} key={option} className={classes.containerOptions}>
            <StyledCheckbox
              data-cy={`checkbox-${option.toLowerCase()}`}
              checked={optionIsChecked(option)}
              className={classes.checkbox}
              onChange={(event) => onSelectOption(option, event.target.checked)}
            />
            <span data-cy={`title-${option.toLowerCase()}`} className={classes.textOption}>
              {option}
            </span>
          </div>
        );
      });
    }

    return null;
  };

  const renderSearchOptions = () => {
    if (!category) return null;

    return (
      <AutoSuggestFilterWrapper>
        <AutoSuggestFilter
          filter={category}
          onSelectOption={onSelectOption}
          onAddOption={onAddOption}
        />
      </AutoSuggestFilterWrapper>
    );
  };

  const renderClearSelected = () => {
    if (category?.options && category.options?.length) {
      return (
        <ListItemText
          primaryTypographyProps={{
            style: {
              fontFamily: "Manrope",
              fontStyle: "normal",
              fontWeight: "500",
              fontSize: "14px",
              lineHeight: "24px",
              color: "#465869",
              cursor: "pointer",
              paddingBottom: "8px",
            },
          }}
          onClick={clearSelectedOptions}
          primary={translate("clearSelection")}
        />
      );
    }
  };

  return (
    <>
      <ListItem
        data-cy={category?.name && category?.name?.toLowerCase()}
        button
        onClick={handleClickOpen}
      >
        <ListItemText
          primaryTypographyProps={{
            style: {
              fontFamily: "Manrope",
              fontStyle: "normal",
              fontWeight: "bold",
              fontSize: "16px",
              lineHeight: "22px",
              color: "#465869",
            },
          }}
          primary={category?.name}
        />
        {open ? <ExpandLess htmlColor="#465869" /> : <ExpandMore htmlColor="#465869" />}
      </ListItem>
      <Collapse
        className={classes.collapse}
        in={open}
        timeout="auto"
        unmountOnExit
        data-cy={`collapse-${category?.name?.toLowerCase()}`}
      >
        {renderOptions()}
        {category?.onSearch ? renderSearchOptions() : null}
        {renderClearSelected()}
      </Collapse>
    </>
  );
};

export default CategoryItem;
